import { getSolutionHotspot, getProcessHotspot } from "@/services/api.service";
import { SquareArrowButton, MoreButton, ImageGallery } from "@/components/ui";
import { mapGetters } from "vuex";
import { FILTER_CONFIG } from "@/constants/filter.constants";

export default {
	name: "SolutionsHostspotModal",
	components: {
		ImageGallery,
		MoreButton,
		SquareArrowButton,
	},
	props: ["lang", "category", "sector", "layer", "hotspotSlug"],
	data() {
		return {
			activeFeature: 0,
			hotspot: null,
			isOpen: false,
			isLoading: true,
			driverModalVisible: false,
			valueDriverId: "",
			isSolutionModal: true,
			isNavigating: false,
			activeAccordion: "valueDrivers",
		};
	},
	created() {
		this.loadHotspot(this.$route.params.hotspotSlug);
	},
	mounted() {
		this.modalContent = this.$refs.modalContent;
	},
	watch: {
		isOpen(newVal) {
			const event = new CustomEvent("modal-state-change", {
				detail: { isOpen: newVal },
			});
			document.dispatchEvent(event);
		},
		"$route"(to, from) {
			if (to.params.hotspotSlug === from?.params?.hotspotSlug && to.params.layer === from?.params?.layer) {
				return;
			}
			this.isLoading = true;
			this.loadHotspot(to.params.hotspotSlug);
		},
	},
	computed: {
		...mapGetters("valueDrivers", ["valueDrivers"]),
		...mapGetters("staticContent", ["getTranslations"]),
		...mapGetters("hotspots", ["hotspots"]),
		...mapGetters("portfolios", ["activePortfolio"]),
		isFullScreen() {
			return this.$store.state.isFullScreen;
		},
		hasActiveFeature() {
			return this.hotspot?.features?.[this.activeFeature];
		},
		hasImages() {
			return (this.hotspot?.images?.length ?? 0) > 0;
		},
		hasIcons() {
			return (this.hotspot?.features?.[this.activeFeature]?.icons?.length ?? 0) > 0;
		},
		images() {
			const imgs = this.hotspot?.images ?? [];
			return imgs.map((img) => {
				return img.url != "" ? img.url : "/images/layout/placeholder.jpeg";
			});
		},
		hotspotTitle() {
			const title = this.hotspot?.title ?? "";
			if (title.length < 27) return title;
			return title.substring(0, 26) + "...";
		},
		baseURL() {
			return this.$router.resolve({
				name: "LayerScreen",
				params: {
					category: this.$route.params.category,
					sector: this.$route.params.sector,
					layer: FILTER_CONFIG.SOLUTIONS_LAYER_ID,
				},
			}).href;
		},
		relatedSolutions() {
			const list = this.hotspot?.related_solutions ?? [];
			return list.map((solution) => ({
				title: solution.title,
				link: `${this.baseURL}/${solution.slug}`,
			}));
		},
		relatedContent() {
			return this.hotspot?.related_content ?? [];
		},
		highlightedSolutions() {
			const list = this.hotspot?.highlighted_solutions ?? [];
			return list.map((solution) => ({
				title: solution.title,
				link: `${this.baseURL}/${solution.slug}`,
			}));
		},
		currentValueDrivers() {
			const driverIds = this.hotspot?.drivers ?? [];
			return this.valueDrivers.filter((driver) => driverIds.includes(driver.id));
		},
		processHotspot() {
			return (
				this.layer === FILTER_CONFIG.FACILITY_ZONES_LAYER_ID ||
				this.layer === FILTER_CONFIG.PROCESS_FLOW_LAYER_ID
			);
		},
		solutionsHotspot() {
			return this.layer === FILTER_CONFIG.SOLUTIONS_LAYER_ID;
		},
		isProcessEquipmentLayer() {
			return this.layer === FILTER_CONFIG.PROCESS_FLOW_LAYER_ID;
		},
	},
	methods: {
		loadHotspot(hotspotSlug) {
			const isProcessLayer =
				this.layer === FILTER_CONFIG.FACILITY_ZONES_LAYER_ID ||
				this.layer === FILTER_CONFIG.PROCESS_FLOW_LAYER_ID;
			const fetchHotspot = isProcessLayer ? getProcessHotspot : getSolutionHotspot;
			fetchHotspot(hotspotSlug)
				.then((data) => {
					this.hotspot = data;
					if (
						this.hotspot.images &&
						typeof this.hotspot.images === "object" &&
						!Array.isArray(this.hotspot.images)
					) {
						this.hotspot.images = Object.values(this.hotspot.images).map((img) => ({
							url: img.url || "/images/layout/placeholder.jpeg",
						}));
					}
					if (!this.hotspot.images || this.hotspot.images.length === 0) {
						this.hotspot.images = [{ url: "/images/layout/placeholder.jpeg" }];
					}
					if (!Array.isArray(this.hotspot.videos)) this.hotspot.videos = [];
					if (!Array.isArray(this.hotspot.related_solutions)) this.hotspot.related_solutions = [];
					if (!Array.isArray(this.hotspot.related_content)) this.hotspot.related_content = [];
					if (!Array.isArray(this.hotspot.highlighted_solutions)) this.hotspot.highlighted_solutions = [];
					if (!Array.isArray(this.hotspot.drivers)) this.hotspot.drivers = [];
					this.isLoading = false;
					this.isOpen = true;
				})
				.catch(() => {
					this.isLoading = false;
				});
		},
		close() {
			this.isOpen = false;
		},
		goBack() {
			this.$router.go(-1);
		},
		setActiveFeature(index) {
			this.activeFeature = index;
			if (this.$refs.modalContent) {
				this.$refs.modalContent.scrollTop = 0;
			}
		},
		runAfterLeave() {
			if (!this.driverModalVisible) {
				this.$router.push({
					name: "LayerScreen",
					params: {
						category: this.$route.params.category,
						sector: this.$route.params.sector,
						layer: this.$route.params.layer,
					},
				});
			}
		},
		openDriverModal(valueDriverId) {
			this.isOpen = false;
			this.driverModalVisible = true;
			this.valueDriverId = valueDriverId;
		},
		handleBackdropClick(event) {
			if (event.target === event.currentTarget) {
				this.close();
			}
		},
		getFilteredProcessEquipmentHotspots() {
			const raw = this.$store.state.hotspots?.hotspots;
			if (!raw || !Array.isArray(raw)) return [];
			const list = Object.values(raw).filter(
				(h) => h.layer === FILTER_CONFIG.PROCESS_FLOW_LAYER_ID && h.orderindex != null
			);
			list.sort((a, b) => a.orderindex - b.orderindex);
			return list;
		},
		prevProcessEquipment() {
			const list = this.getFilteredProcessEquipmentHotspots();
			if (list.length === 0) return;
			const slug = this.$route.params.hotspotSlug || this.hotspot?.slug;
			const idx = list.findIndex((h) => h.slug === slug);
			const targetIndex = idx <= 0 ? list.length - 1 : idx - 1;
			this.isNavigating = true;
			this.navigateToHotspot(list[targetIndex]);
			this.isNavigating = false;
		},
		nextProcessEquipment() {
			const list = this.getFilteredProcessEquipmentHotspots();
			if (list.length === 0) return;
			const slug = this.$route.params.hotspotSlug || this.hotspot?.slug;
			const idx = list.findIndex((h) => h.slug === slug);
			const targetIndex = idx < 0 || idx === list.length - 1 ? 0 : idx + 1;
			this.isNavigating = true;
			this.navigateToHotspot(list[targetIndex]);
			this.isNavigating = false;
		},
		navigateToHotspot(hotspot) {
			this.$router.push({
				name: "HotspotModal",
				params: {
					lang: this.$route.params.lang,
					category: this.$route.params.category,
					sector: this.$route.params.sector,
					layer: hotspot.layer,
					hotspotSlug: hotspot.slug,
				},
			});
		},
		async getFilteredHotspots() {
			const hotspots = Object.values(this.$store.state.hotspots.hotspots);
			hotspots.sort((a, b) => a.orderindex - b.orderindex);
			return hotspots.filter(
				(hotspot) =>
					hotspot.layer === FILTER_CONFIG.SOLUTIONS_LAYER_ID && hotspot.orderindex !== null
			);
		},
		async findHotspotByOrderIndex(orderindex) {
			const filtered_hotspots = await this.getFilteredHotspots();
			const hotspot_index = filtered_hotspots.findIndex(
				(hotspot) => hotspot.orderindex === orderindex
			);

			return hotspot_index !== -1 ? filtered_hotspots[hotspot_index] : null;
		},
		async copyLinkToClipboard() {
			try {
				const current_url = window.location.href;
				await navigator.clipboard.writeText(current_url);
				
				const button = this.$refs.copyButton;
				const text_span = button.querySelector('span');
				if (text_span) {
					const original_text = text_span.textContent;
					text_span.textContent = this.getTranslations("copied");
					
					setTimeout(() => {
						text_span.textContent = original_text;
					}, 2000);
				}
			} catch (err) {
				const text_area = document.createElement('textArea');
				text_area.value = window.location.href;
				document.body.appendChild(text_area);
				text_area.select();
				document.execCommand('copy');
				document.body.removeChild(text_area);
			}
		}
	},
};